<script setup lang="ts">
import { computed, markRaw, nextTick, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import cloneDeep from "lodash.clonedeep";
import moment from "moment-timezone";

import { isset } from "@/helpers/object";

import type { ButtonAltProps } from "@/dsComponents/buttons/types";
import type { DatePickerProps } from "@/dsComponents/DatePicker/types";
import type { TableAltCustomElement, TableAltRows } from "@/dsComponents/TableAlt/types";
import type { HourInputProps } from "./HourInput/types";
import type { DateRange } from "./types";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Link from "@/dsComponents/buttons/Link.vue";
import DatePicker from "@/dsComponents/DatePicker/DatePicker.vue";
import TableAlt from "@/dsComponents/TableAlt/TableAlt.vue";
import ToggleLabel from "@/dsComponents/Toggle/ToggleLabel.vue";

import { TABLE_DATE_FORMAT } from "./constants";
import HourInput from "./HourInput/HourInput.vue";
import { getHourIndexFromValue } from "./helper";

interface DateRangeTableProps {
    dateRanges: DateRange[];
    minDate?: Date;
}

const props = defineProps<DateRangeTableProps>();
const { t } = useI18n();

const emit = defineEmits<(name: "ranges", dateRanges: DateRange[]) => void>();

const dataToModify = ref<{ field: string; index: number }>();
const tableDateRanges = ref<TableAltRows[]>([]);

// The calendar opens on the selected month: when it is before the min date, min/max
// navigation would lock every arrow, so the user could not reach an allowed month
const isBeforeMinDate = (date: moment.Moment | null, minDate?: Date): boolean =>
    Boolean(date && minDate && date.isBefore(minDate, "day"));

const generateRow = (
    date: { start: number | null; end: number | null },
    time?: { start: string | null; end: string | null },
    weekendIncluded?: boolean
) => {
    const { start = "930", end = "1700" } = time ?? {};
    const startDate = date.start ? moment(date.start) : null;
    const endDate = date.end ? moment(date.end) : null;
    const endDateIsBeforeStartDate = endDate && startDate && endDate.isBefore(startDate, "day");
    const endMinDate =
        startDate && (!props.minDate || startDate.isAfter(props.minDate, "day")) ? startDate.toDate() : props.minDate;

    const hasWeekend = startDate && endDate ? containsWeekend(startDate, endDate) : false;

    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    const rowContent: any = {
        startDate: {
            customElement: {
                component: markRaw(DatePicker),
                props: {
                    icon: "cal",
                    format: TABLE_DATE_FORMAT,
                    value: startDate,
                    placeholder: "Début",
                    unactivate: true,
                    variant: "ghost",
                    minDate: props.minDate,
                    preventMinMaxNavigation: !isBeforeMinDate(startDate, props.minDate),
                } as DatePickerProps,
                action: (index: number) => {
                    dataToModify.value = { field: "startDate", index };
                },
            },
        },
        endDate: {
            customElement: {
                component: markRaw(DatePicker),
                props: {
                    icon: "arrow-right",
                    format: TABLE_DATE_FORMAT,
                    value: endDate,
                    placeholder: "Fin",
                    unactivate: true,
                    variant: "ghost",
                    error: endDateIsBeforeStartDate,
                    minDate: endMinDate,
                    startDate: startDate ? startDate.toDate() : undefined,
                    preventMinMaxNavigation: !isBeforeMinDate(endDate ?? startDate, endMinDate),
                } as DatePickerProps,
                action: (index: number) => {
                    dataToModify.value = { field: "endDate", index };
                },
            },
        },
        startTime: {
            customElement: {
                component: markRaw(HourInput),
                props: {
                    icon: "clock",
                    value: start,
                    placeholder: "Début",
                } as HourInputProps,
                action: (index: number) => {
                    dataToModify.value = { field: "startTime", index };
                },
            },
        },
        endTime: {
            customElement: {
                component: markRaw(HourInput),
                props: {
                    icon: "arrow-right",
                    value: end,
                    placeholder: "Fin",
                    error: Boolean(start && end && endDate?.isSame(startDate) && Number(start) > Number(end)),
                    scrollTo: start && !end ? getHourIndexFromValue(start) : undefined,
                } as HourInputProps,
                action: (index: number) => {
                    dataToModify.value = { field: "endTime", index };
                },
            },
        },
    };

    if (hasWeekend) {
        rowContent.weekendIncluded = createWeekendToggle(weekendIncluded ?? false);
    }

    return {
        content: rowContent,
    } as TableAltRows;
};

const generateEmptyRow = (): TableAltRows => {
    return {
        content: {
            startDate: {
                customElement: {
                    component: markRaw(DatePicker),
                    props: {
                        icon: "cal",
                        format: TABLE_DATE_FORMAT,
                        placeholder: t("global.start"),
                        value: null,
                        size: "xs",
                        unactivate: true,
                        variant: "ghost",
                        minDate: props.minDate,
                    } as DatePickerProps,
                    action: (index: number) => {
                        dataToModify.value = { field: "startDate", index };
                    },
                },
            },
            endDate: {
                customElement: {
                    component: markRaw(DatePicker),
                    props: {
                        icon: "arrow-right",
                        format: TABLE_DATE_FORMAT,
                        placeholder: t("global.end"),
                        value: null,
                        size: "xs",
                        unactivate: true,
                        variant: "ghost",
                        minDate: props.minDate,
                    } as DatePickerProps,
                    action: (index: number) => {
                        dataToModify.value = { field: "endDate", index };
                    },
                },
            },
            startTime: {
                customElement: {
                    component: markRaw(HourInput),
                    props: {
                        icon: "clock",
                        placeholder: t("global.start"),
                        value: "",
                    } as HourInputProps,
                    action: (index: number) => {
                        dataToModify.value = { field: "startTime", index };
                    },
                },
            },
            endTime: {
                customElement: {
                    component: markRaw(HourInput),
                    props: {
                        icon: "arrow-right",
                        placeholder: t("global.end"),
                        value: "",
                    } as HourInputProps,
                    action: (index: number) => {
                        dataToModify.value = { field: "endTime", index };
                    },
                },
            },
        },
    } as TableAltRows;
};

const isOnlyWeekendRange = (startDate?: moment.Moment, endDate?: moment.Moment): boolean => {
    if (!startDate || !endDate) return false;
    const tempDate = startDate.clone();
    const daysDiff = endDate.diff(startDate, "days");
    for (let i = 0; i <= daysDiff; i++) {
        const currentWeekday = tempDate.isoWeekday();
        if (currentWeekday !== 6 && currentWeekday !== 7) {
            return false;
        }
        tempDate.add(1, "day");
    }
    return true;
};

const isFridayToWeekendRange = (startDate?: moment.Moment, endDate?: moment.Moment): boolean => {
    if (!startDate || !endDate) return false;
    const startWeekday = startDate.isoWeekday();
    const endWeekday = endDate.isoWeekday();
    const startsOnFriday = startWeekday === 5;
    const endsOnWeekend = endWeekday === 6 || endWeekday === 7;
    return startsOnFriday && endsOnWeekend;
};

const manageToggleWeekend = (dateRanges: DateRange[], options?: { respectProp?: boolean; autoActivateIndex?: number }) => {
    const { respectProp = false, autoActivateIndex } = options ?? {};

    tableDateRanges.value = convertDateRangeToTableDateRange(dateRanges);

    dateRanges.forEach((dr, index) => {
        if (dr.startDate && dr.endDate) {
            const startDate = moment(dr.startDate).startOf("day");
            const endDate = moment(dr.endDate).endOf("day");
            const endDateIsBeforeStartDate = endDate.isBefore(startDate, "day");
            if (!endDateIsBeforeStartDate) {
                const hasWeekend = containsWeekend(startDate, endDate);

                if (hasWeekend) {
                    if (!tableDateRanges.value[index].content.weekendIncluded) {
                        tableDateRanges.value[index].content.weekendIncluded = createWeekendToggle(false);
                    }
                } else {
                    if (tableDateRanges.value[index].content.weekendIncluded) {
                        // biome-ignore lint/style/noNonNullAssertion: Init Biome
                        tableDateRanges.value[index].content.weekendIncluded.customElement!.props.active = false;
                        delete tableDateRanges.value[index].content.weekendIncluded;
                    }
                }

                if (tableDateRanges.value[index].content.weekendIncluded) {
                    if (respectProp) {
                        // biome-ignore lint/style/noNonNullAssertion: Init Biome
                        tableDateRanges.value[index].content.weekendIncluded.customElement!.props.active = Boolean(dr.weekendIncluded);
                    } else if (autoActivateIndex === index) {
                        const onlyWeekend = isOnlyWeekendRange(startDate, endDate);
                        const fridayToWeekend = isFridayToWeekendRange(startDate, endDate);
                        if (onlyWeekend || fridayToWeekend) {
                            // biome-ignore lint/style/noNonNullAssertion: Init Biome
                            tableDateRanges.value[index].content.weekendIncluded.customElement!.props.active = true;
                        }
                    }
                }
            }
        }
    });
    tableDateRanges.value = [...tableDateRanges.value];
    return tableDateRanges.value;
};

const convertTableDateRangeToDateRange = (rows: TableAltRows[]): DateRange[] => {
    return rows.map((row) => {
        const range = {} as DateRange;
        const { startDate, endDate, startTime, endTime, weekendIncluded } = row.content;
        const getDate = (date: string | undefined): number | null => (date ? moment(date, TABLE_DATE_FORMAT).valueOf() : null);
        range.startDate = getDate(startDate.customElement?.props?.value);
        range.endDate = getDate(endDate.customElement?.props?.value);
        range.startTime = startTime.customElement?.props?.value ?? "";
        range.endTime = endTime.customElement?.props?.value ?? "";
        range.weekendIncluded = weekendIncluded?.customElement?.props?.active ?? false;
        return range;
    });
};

const convertDateRangeToTableDateRange = (rows: DateRange[]): TableAltRows[] => {
    return rows.map((row) =>
        generateRow({ start: row.startDate, end: row.endDate }, { start: row.startTime, end: row.endTime }, row.weekendIncluded)
    );
};

const addEmptyRow = () => {
    tableDateRanges.value = [...tableDateRanges.value, generateEmptyRow()];
    emit("ranges", convertTableDateRangeToDateRange(tableDateRanges.value));
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const containsWeekend = (startDate: any, endDate: any): boolean => {
    if (!startDate || !endDate) return false;

    const tempDate = startDate.clone();
    const daysDiff = endDate.diff(startDate, "days");

    for (let i = 0; i <= daysDiff; i++) {
        const currentWeekday = tempDate.isoWeekday();
        if (currentWeekday === 6 || currentWeekday === 7) {
            return true;
        }
        tempDate.add(1, "day");
    }
    return false;
};

const createWeekendToggle = (active: boolean = false) => ({
    customElement: {
        component: markRaw(ToggleLabel),
        props: {
            active,
            label: t("page.order.includedWeekend"),
            size: "sm",
        },
        action: (index: number) => {
            dataToModify.value = { field: "weekendIncluded", index };
        },
    },
});

const resetEndDateIfBeforeStart = (dateRangeCopy: TableAltRows[], index: number) => {
    const ranges = convertTableDateRangeToDateRange(dateRangeCopy);
    const range = ranges[index];
    if (range.startDate && range.endDate && moment(range.endDate).isBefore(moment(range.startDate), "day")) {
            // biome-ignore lint/style/noNonNullAssertion: Init Biome
        dateRangeCopy[index].content.endDate.customElement!.props.value = null;
    }
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const handleDateAndHourChange = async (value: any) => {
    await nextTick();

    let dateRangeCopy = cloneDeep(tableDateRanges.value);
    if (isset(dataToModify.value?.index) && dataToModify.value?.field) {
        if (dateRangeCopy[dataToModify.value.index].content[dataToModify.value.field].customElement) {
            // biome-ignore lint/style/noNonNullAssertion: Init Biome
            dateRangeCopy[dataToModify.value.index].content[dataToModify.value.field].customElement!.props.value = value as string;

            if (dataToModify.value.field === "startDate") {
                resetEndDateIfBeforeStart(dateRangeCopy, dataToModify.value.index);
            }

            dateRangeCopy = manageToggleWeekend(convertTableDateRangeToDateRange(dateRangeCopy), {
                autoActivateIndex: dataToModify.value.index,
            });
            emit("ranges", convertTableDateRangeToDateRange(dateRangeCopy));
        }
    }
};

const handleWeekendChange = () => {
    if (dataToModify.value?.field !== "weekendIncluded") return;

    const dateRangeCopy = cloneDeep(tableDateRanges.value);
    if (isset(dataToModify.value?.index) && dataToModify.value?.field) {
        if (dateRangeCopy[dataToModify.value.index].content.weekendIncluded.customElement) {
            // biome-ignore lint/style/noNonNullAssertion: Init Biome
            const active = dateRangeCopy[dataToModify.value.index].content.weekendIncluded.customElement!.props.active;
            // biome-ignore lint/style/noNonNullAssertion: Init Biome
            dateRangeCopy[dataToModify.value.index].content.weekendIncluded.customElement!.props.active = !active;
            tableDateRanges.value = [...dateRangeCopy];
            emit("ranges", convertTableDateRangeToDateRange(dateRangeCopy));
        }
    }
};
watch(
    () => props.dateRanges,
    (newDateRanges) => {
        manageToggleWeekend(newDateRanges, { respectProp: true });
    },
    { immediate: true, deep: true }
);

onBeforeMount(() => {
    if (!props.dateRanges?.length) {
        tableDateRanges.value = [generateEmptyRow()];
        emit("ranges", convertTableDateRangeToDateRange(tableDateRanges.value));
    } else {
        manageToggleWeekend(props.dateRanges, { respectProp: true });
    }
});

const dateColumn = [
    { field: "startDate", width: "150px" },
    { field: "endDate", width: "180px" },
    { field: "startTime", width: "92px" },
    { field: "endTime", width: "92px" },
    { field: "weekendIncluded", width: "84px" },
];

const dateRowActions = computed<TableAltCustomElement<ButtonAltProps>[]>(() => {
    const actions: TableAltCustomElement<ButtonAltProps>[] = [
        {
            component: markRaw(ButtonAlt),
            props: { type: "neutral", iconOnly: "copy" },
            action: (index: number) => {
                const newRanges = [...props.dateRanges, cloneDeep(props.dateRanges[index])];
                emit("ranges", newRanges);
                tableDateRanges.value = convertDateRangeToTableDateRange(newRanges);
            },
        },
    ];

    if (props.dateRanges.length > 1) {
        actions.push({
            component: markRaw(ButtonAlt),
            props: { type: "error", iconOnly: "trash" },
            action: (index: number) => {
                const newRanges = props.dateRanges.filter((_, dateRangeIndex) => dateRangeIndex !== index);
                emit("ranges", newRanges);
                tableDateRanges.value = convertDateRangeToTableDateRange(newRanges);
            },
        });
    }

    return actions;
});
</script>
<template>
    <div class="DateRangeTable">
        <TableAlt
            :rows="tableDateRanges"
            :columns="dateColumn"
            :global-actions="{ rowActions: dateRowActions }"
            left-padding="0px"
            right-padding="0px"
            cell-left-padding="4px"
            cell-right-padding="4px"
            @select-hour="handleDateAndHourChange"
            @date-picked="handleDateAndHourChange"
            @input="handleWeekendChange"
            row-hover
        >
            <template #bottom-slot>
                <div class="link-container">
                    <Link type="neutral" size="xs" left-icon="plus" @click="addEmptyRow">Ajouter une période</Link>
                </div>
            </template>
        </TableAlt>
    </div>
</template>
<style scoped lang="scss">
.DateRangeTable {
    display: flex;
    flex-direction: column;
    gap: 10px;
    .link-container {
        padding-left: 4px;
        padding-right: 4px;
    }
    .toggle-container {
        padding-left: 8px;
    }
}
</style>
